// === src/components/WATareasList.js ===
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, Menu, MenuItem, Tooltip, InputAdornment, Chip, Divider,
    Checkbox, Snackbar, Alert, FormControlLabel, TablePagination
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Navbar from "../Navbar";
import { Visibility, VisibilityOff, CheckCircle, Cancel, MoreVert, Search, AccessTime, Download, Assessment, Refresh, ContentCopy, Phone, Business, NotificationsActive, Schedule, ThumbUp, ThumbDown, ViewList } from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import useMqttSubscription from "../../Hooks/useMqttSubscription";
import WATareasStats from "./WATareasStats";

import "dayjs/locale/es";
import { titulonavbar } from "../../services/apirest";

dayjs.extend(customParseFormat);
dayjs.locale("es");


export default function WATareasList() {
    const API = (localStorage.getItem("API") || "https://wabot.netizar.com").replace(/\/+$/, "");

    // 🆕 Crear instancia de axios con interceptor para token dinámico
    // 🆕 Crear instancia de axios con interceptores para token dinámico y manejo de expiración
    const api = useMemo(() => {
        const instance = axios.create({
            baseURL: API,
        });

        // 📤 Interceptor de REQUEST: Agrega el token actual en cada petición
        instance.interceptors.request.use((config) => {
            const token = localStorage.getItem("token");
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });

        // 📥 Interceptor de RESPONSE: Maneja errores de autenticación
        instance.interceptors.response.use(
            (response) => response, // Si todo OK, continuar normal
            (error) => {
                // Si el token expiró o es inválido
                if (error.response?.status === 403 || error.response?.status === 401) {
                    const errorMsg = error.response?.data?.error || "";

                    // Verificar si es error de token
                    if (errorMsg.includes("Token") || errorMsg.includes("token") ||
                        errorMsg.includes("expirado") || errorMsg.includes("inválido")) {

                        console.warn("⚠️ Token expirado o inválido. Redirigiendo al login...");

                        // Limpiar sesión
                        localStorage.removeItem("token");
                        localStorage.removeItem("user");
                        localStorage.removeItem("sector");

                        // Mostrar alerta
                        alert("Tu sesión ha expirado. Por favor, volvé a iniciar sesión.");

                        // Redirigir al login
                        window.location.href = "/login";
                    }
                }

                return Promise.reject(error);
            }
        );

        return instance;
    }, [API]);

    const usuario = localStorage.getItem("user") || "RRHH";
    const sector = (localStorage.getItem("sector") || "1").trim();

    const [tareas, setTareas] = useState([]);
    const [selected, setSelected] = useState(null);
    const [observacion, setObservacion] = useState("");
    const [enviarNotif, setEnviarNotif] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState(localStorage.getItem("wa_search") || "");
    const [estadoChip, setEstadoChip] = useState(localStorage.getItem("wa_estado") || "pendiente");
    const [nowTick, setNowTick] = useState(Date.now());
    const [dniRegistro, setDniRegistro] = useState("");
    const [nombreRegistro, setNombreRegistro] = useState("");
    const [statsOpen, setStatsOpen] = useState(false);
    const [fechaRevision, setFechaRevision] = useState(null);
    const [esRevisionFinal, setEsRevisionFinal] = useState(false);

    // 🆕 Estados de paginación
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    // 🆕 Ref para auto-scroll de la tabla
    const tableContainerRef = useRef(null);

    const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });
    const handleSnackClose = (_, reason) => {
        if (reason === "clickaway") return;
        setSnack((s) => ({ ...s, open: false }));
    };

    const [archivosModal, setArchivosModal] = useState({ open: false, archivos: [], total: 0, tareaId: null });
    // ---- Cargar tareas ----
    const fetchTareas = async () => {
        try {
            const { data } = await api.get("/api/tareas");
            setTareas(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ Error al cargar tareas:", err);
        }
    };
    useEffect(() => { fetchTareas(); }, []);

    const { connectionStatus } = useMqttSubscription(
        "netizar/wa/tareas",   // topic
        (payload) => {
            console.log("📩 MQTT → actualización recibida:", payload);
            fetchTareas();        // refresca lista de tareas
        }
    );


    const normalize = (s) =>
        (s || "")
            .toString()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .trim();

    const formatRelativeEs = (from, now = Date.now()) => {
        if (!from) return "-";
        const date = new Date(from);
        // ✅ NO sumar horas - la BD ya guarda en hora local
        const diff = Math.max(0, now - date.getTime());
        const m = Math.floor(diff / 60000);
        if (m < 1) return "hace unos segundos";
        if (m < 60) return `hace ${m} min`;
        const h = Math.floor(m / 60);
        if (h < 24) return `hace ${h} h`;
        const d = Math.floor(h / 24);
        if (d < 7) return `hace ${d} día${d > 1 ? "s" : ""}`;
        return date.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" });
    };

    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);

    // Helper: ¿la tarea es de Medicina Laboral?
    const isML = (t) =>
        String(t?.visible_para_sector || "")
            .split(",")
            .map((v) => v.trim())
            .includes("16");

    // Base visible por sector (si el usuario es sector 16, solo ve ML)
    const baseLista = useMemo(() => {
        if (sector === "16") {
            return tareas.filter(isML);
        }
        return tareas;
    }, [tareas, sector]);

    // Contadores (sobre la lista base visible)
    const counters = useMemo(() => {
        const base = { pendiente: 0, resuelto: 0, cancelada: 0, aprobado: 0, rechazado: 0, revisiones: 0 };
        for (const t of baseLista) {
            const est = (t.estado || "").toLowerCase();
            const res = (t.resolucion || "").toLowerCase();
            if (est === "pendiente") base.pendiente++;
            if (est === "resuelto") base.resuelto++;
            if (est === "cancelada") base.cancelada++;
            if (res === "aprobado") base.aprobado++;
            if (res === "rechazado") base.rechazado++;
            if (t.fue_reactivada === 1) base.revisiones++;
        }
        return base;
    }, [baseLista]);

    // Filtros (estado + búsqueda) sobre la base visible por sector
    const tareasFiltradas = useMemo(() => {
        let lista = [...baseLista];
        const chip = (estadoChip || "").toLowerCase();

        if (chip && chip !== "todas") {
            if (["pendiente", "resuelto", "cancelada"].includes(chip)) {
                lista = lista.filter((t) => (t.estado || "").toLowerCase() === chip);
            } else if (chip === "aprobado" || chip === "rechazado") {
                lista = lista.filter((t) => (t.resolucion || "").toLowerCase() === chip);
            } else if (chip === "revisiones") {
                lista = lista.filter((t) => t.fue_reactivada === 1);
            }
        }

        const q = normalize(search);
        if (q) {
            lista = lista.filter((t) => {
                const texto = normalize(
                    [
                        t.id, t.numero, t.dni, t.nombre, t.apellido, t.sucursal,
                        t.tipo_detalle, t.mensaje, t.observacion, t.estado, t.resolucion,
                    ].filter(Boolean).join(" ")
                );
                return texto.includes(q);
            });
        }
        return lista;
    }, [baseLista, search, estadoChip]);

    // 🆕 Aplicar paginación DESPUÉS del filtrado
    const tareasPaginadas = useMemo(() => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return tareasFiltradas.slice(start, end);
    }, [tareasFiltradas, page, rowsPerPage]);

    // 🆕 Handlers de paginación
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Volver a la primera página
    };

    const openModal = (row) => {
        setSelected(row);
        setObservacion("");
        setEnviarNotif(false);

        if (row?.tipo_detalle === "registro_pendiente") {
            setDniRegistro(row.dni || "");
            const nombreCompleto = [row.nombre, row.apellido].filter(Boolean).join(" ").trim();
            setNombreRegistro(nombreCompleto);
        } else {
            setDniRegistro("");
            setNombreRegistro("");
        }

        // Inicializar campos de revisión
        setFechaRevision(null);
        setEsRevisionFinal(false);
    };

    const closeModal = () => {
        setSelected(null);
        setObservacion("");
        setEnviarNotif(false);
        setDniRegistro("");
        setNombreRegistro("");
        setFechaRevision(null);
        setEsRevisionFinal(false);
    };

    // 🔍 Resolver / aprobar / rechazar / revertir
    const handleResolver = async (accion = "toggle") => {

        // ============================
        // 🟦 CASO ESPECIAL: REGISTRO
        // ============================
        if (selected?.tipo_detalle === "registro_pendiente") {
            let nuevaResolucion = null;
            let nuevoEstado = "resuelto";

            if (accion === "registro_aprobar") {
                nuevaResolucion = "aprobado";
            } else if (accion === "registro_rechazar") {
                nuevaResolucion = "rechazado";
            } else {
                return;
            }

            try {
                await api.post(`/api/tareas/${selected.id}/resolver`, {
                    usuario_accion: usuario,
                    resolucion: nuevaResolucion,
                    estado: nuevoEstado,
                    observacion,
                    enviar_notificacion: false,
                    dni: dniRegistro || null,
                    nombre: nombreRegistro || null,
                });

                setSnack({
                    open: true,
                    msg:
                        nuevaResolucion === "aprobado"
                            ? "✅ Usuario activado correctamente"
                            : "⛔ Registro rechazado",
                    severity: nuevaResolucion === "aprobado" ? "success" : "warning",
                });

                closeModal();
                fetchTareas();
            } catch (err) {
                console.error("❌ Error resolviendo tarea de registro:", err);
                alert("Error al actualizar tarea de registro.");
            }

            return; // 👈 DETIENE LA EJECUCIÓN
        }



        // ============================================
        // 🟩 CASOS NORMALES DE MEDICINA LABORAL (ML)
        // ============================================
        if (!selected) return;

        let nuevoEstado = (selected.estado || "").toLowerCase();
        let nuevaResolucion = selected.resolucion || null;

        if (sector === "16") {
            if (accion === "aprobado" || accion === "rechazado") {
                nuevaResolucion = accion;
                nuevoEstado = "pendiente";
            } else if (accion === "revertir") {
                nuevoEstado = "pendiente";
                nuevaResolucion = null;
            } else {
                nuevoEstado = nuevoEstado === "pendiente" ? "resuelto" : "pendiente";
            }
        } else {
            if (accion === "toggle") {
                nuevoEstado = nuevoEstado === "pendiente" ? "resuelto" : "pendiente";
            } else {
                nuevoEstado = "resuelto";
            }
        }

        // ============================
        // 🟧 TAREAS NORMALES (RRHH)
        // ============================
        try {
            await api.post(`/api/tareas/${selected.id}/resolver`, {
                usuario_accion: usuario,
                resolucion: nuevaResolucion,
                estado: nuevoEstado,
                observacion,
                enviar_notificacion: enviarNotif,
                fecha_revision: fechaRevision ? dayjs(fechaRevision).format('YYYY-MM-DD') : null,
                es_revision_final: esRevisionFinal ? 1 : 0,
            });

            if (sector === "16") {
                if (accion === "aprobado") {
                    setSnack({ open: true, msg: "✅ Tarea aprobada", severity: "success" });
                } else if (accion === "rechazado") {
                    setSnack({ open: true, msg: "⛔ Tarea rechazada", severity: "warning" });
                } else if (accion === "revertir") {
                    setSnack({ open: true, msg: "↩️ Tarea revertida a pendiente", severity: "info" });
                } else {
                    setSnack({ open: true, msg: "✔️ Tarea actualizada", severity: "success" });
                }
            } else {
                const estabaPendiente = (selected?.estado || "").toLowerCase() === "pendiente";
                setSnack({
                    open: true,
                    msg: estabaPendiente ? "✅ Tarea marcada como resuelta" : "↩️ Tarea vuelta a pendiente",
                    severity: estabaPendiente ? "success" : "info",
                });
            }

            closeModal();
            fetchTareas();
        } catch (err) {
            console.error("❌ Error resolviendo tarea:", err);
            alert("Error al actualizar la tarea. Ver consola.");
        }
    };


    const verArchivo = async (rutaArchivo) => {
        if (!rutaArchivo) return;

        try {
            console.log("🔍 Abriendo archivo:", rutaArchivo);

            // Hacer petición con token (api ya tiene headers configurados)
            const res = await api.get(rutaArchivo, {
                responseType: "blob"
            });

            console.log("✅ Archivo descargado correctamente");

            // Crear el blob
            const blob = new Blob([res.data], {
                type: res.headers["content-type"] || "application/octet-stream",
            });

            const blobUrl = URL.createObjectURL(blob);

            // Abrir en nueva ventana
            const newWindow = window.open();
            if (newWindow) {
                newWindow.opener = null;
                newWindow.location = blobUrl;
            }

            // Limpieza después de 8 segundos
            setTimeout(() => URL.revokeObjectURL(blobUrl), 8000);

        } catch (err) {
            console.error("❌ Error al abrir archivo:", err);

            if (err.response?.status === 401) {
                alert("⚠️ Sesión expirada.\n\nPor favor, recargá la página (F5) e iniciá sesión nuevamente.");
            } else {
                alert("❌ No se pudo abrir el archivo.\n\nIntentá nuevamente o contactá al administrador.");
            }
        }
    };

    // 📞 Copiar teléfono al portapapeles con formato internacional
    const copiarTelefono = async (telefono) => {
        if (!telefono) return;

        try {
            // Limpiar el número (quitar espacios, guiones, paréntesis)
            let numeroLimpio = telefono.toString().replace(/[\s\-()]/g, '');

            // Si NO empieza con +, agregarlo
            if (!numeroLimpio.startsWith('+')) {
                numeroLimpio = '+' + numeroLimpio;
            }

            // Copiar al portapapeles
            await navigator.clipboard.writeText(numeroLimpio);

            // Mostrar feedback
            setSnack({
                open: true,
                msg: `📞 Teléfono copiado: ${numeroLimpio}`,
                severity: "success"
            });

        } catch (err) {
            console.error("❌ Error al copiar teléfono:", err);
            setSnack({
                open: true,
                msg: "❌ No se pudo copiar el teléfono",
                severity: "error"
            });
        }
    };

    const verArchivosMultiples = async (tareaId) => {
        try {
            console.log(`📎 Cargando archivos para tarea #${tareaId}`);

            const { data } = await api.get(`/api/tareas/${tareaId}/archivos`);

            console.log(`✅ Recibidos ${data.length} archivos:`, data);

            setArchivosModal({
                open: true,
                archivos: data,
                total: data.length,
                tareaId: tareaId
            });
        } catch (err) {
            console.error("❌ Error cargando archivos:", err);
            setSnack({
                open: true,
                msg: "Error al cargar archivos: " + (err.response?.data?.error || err.message),
                severity: "error"
            });
        }
    };

    const cerrarModalArchivos = () => {
        setArchivosModal({
            open: false,
            archivos: [],
            total: 0,
            tareaId: null
        });
    };

    const descargarArchivo = async (rutaArchivo, isRetry = false) => {
        if (!rutaArchivo) return;

        try {
            // 🆕 Obtener token fresco
            const tokenFresco = localStorage.getItem("token");

            console.log("⬇️ Descargando:", rutaArchivo);

            // Descargar con token fresco
            const res = await api.get(rutaArchivo, {
                responseType: "blob",
                headers: {
                    Authorization: `Bearer ${tokenFresco}`
                }
            });

            // Crear blob
            const blob = new Blob([res.data], {
                type: res.headers["content-type"] || "application/octet-stream",
            });

            const blobUrl = URL.createObjectURL(blob);

            // Extraer nombre del archivo
            const nombreArchivo = rutaArchivo.split("/").pop() || "archivo_descargado";

            // Crear link temporal para forzar descarga
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = nombreArchivo;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Limpieza
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

        } catch (err) {
            console.error("❌ Error al descargar archivo:", err);

            // 🆕 Retry automático en error 403/401
            if (!isRetry && (err.response?.status === 403 || err.response?.status === 401)) {
                console.warn("⚠️ Error de autenticación, reintentando descarga en 2 segundos...");

                await new Promise(resolve => setTimeout(resolve, 2000));

                try {
                    await fetchTareas();
                } catch (e) {
                    console.warn("No se pudo refrescar tareas");
                }

                return descargarArchivo(rutaArchivo, true);
            }

            if (isRetry) {
                alert("No se pudo descargar el archivo después de reintentar.\n\nPor favor, recargá la página (F5) e intentá nuevamente.");
            } else {
                alert("No se pudo descargar el archivo. Intentá nuevamente.");
            }
        }
    };

    const handleMenuOpen = (event, row) => {
        setAnchorEl(event.currentTarget);
        setSelected(row);
    };
    const handleMenuClose = () => setAnchorEl(null);
    const handleAccion = (accion) => {
        handleMenuClose();
        if (accion === "resolver") openModal(selected);
    };

    useEffect(() => { localStorage.setItem("wa_search", search); }, [search]);
    useEffect(() => { localStorage.setItem("wa_estado", estadoChip); }, [estadoChip]);

    // 🆕 Resetear página cuando cambian filtros
    useEffect(() => {
        setPage(0);
    }, [estadoChip, search]);

    // 🆕 Auto-scroll al inicio cuando cambian filtros o página
    useEffect(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, [page, estadoChip, search]);

    // Estilos de columnas (ancho fijo) y detalle con wrap
    const colSx = {
        thId: { width: 70, maxWidth: 70 },
        thNum: { width: 220, maxWidth: 220 },
        thDet: { width: "auto" }, // se expande y hace wrap
        thArc: { width: 100, maxWidth: 100 },
        thEst: { width: 130, maxWidth: 130 },
        thFec: { width: 170, maxWidth: 170, whiteSpace: "nowrap" },
        thAcc: { width: 100, maxWidth: 100, textAlign: "center" },
        tdDet: { whiteSpace: "normal", wordBreak: "break-word" },
    };

    const getRowColor = (t) => {
        const estado = (t.estado || "").toLowerCase();
        const resolucion = (t.resolucion || "").toLowerCase();

        // Es tarea de Medicina Laboral si visible_para_sector incluye 16
        const isMLTask = String(t.visible_para_sector || "")
            .split(",")
            .map((v) => v.trim())
            .includes("16");

        if (isMLTask) {
            // ✅ Aprobado por ML → verde
            if (resolucion === "aprobado") return "#C8E6C9"; // verde suave

            // ❌ Rechazado por ML → rojo
            if (resolucion === "rechazado") return "#FFCDD2"; // rojo suave

            // 🟧 Pendiente ML (sin resolución) → naranja
            return "#FFE0B2"; // naranja suave
        }

        // 👉 Resto de tareas (RRHH normal) mantiene esquema anterior / neutro
        if (estado === "pendiente") return "#FFFDE7"; // amarillo muy claro
        if (estado === "resuelto") return "#E8F5E9";  // verde muy claro

        return "inherit";
    };

    if (titulonavbar === 'DevExt') {
        return (
            <>
                <Navbar />
                <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="h5" color="error" sx={{ mt: 4 }}>
                        ⚠️ Panel no disponible en desarrollo
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Este panel solo funciona en el entorno de producción.
                    </Typography>
                </Box>
            </>
        );
    }


    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                {/* HEADER */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={2}>
                    <Typography variant="h6">📋 Tareas WhatsApp</Typography>

                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                        {/* Filtros con iconos y badges */}
                        {[
                            {
                                key: "pendiente",
                                icon: Schedule,
                                tooltip: "Tareas pendientes",
                                color: "#FF9800",
                                count: counters.pendiente
                            },
                            {
                                key: "resuelto",
                                icon: CheckCircle,
                                tooltip: "Tareas resueltas",
                                color: "#4CAF50",
                                count: counters.resuelto
                            },
                            {
                                key: "cancelada",
                                icon: Cancel,
                                tooltip: "Tareas canceladas",
                                color: "#F44336",
                                count: counters.cancelada
                            },
                            {
                                key: "aprobado",
                                icon: ThumbUp,
                                tooltip: "Tareas aprobadas",
                                color: "#2E7D32",
                                count: counters.aprobado
                            },
                            {
                                key: "rechazado",
                                icon: ThumbDown,
                                tooltip: "Tareas rechazadas",
                                color: "#C62828",
                                count: counters.rechazado
                            },
                            {
                                key: "revisiones",
                                icon: NotificationsActive,
                                tooltip: "Revisiones programadas",
                                color: "#FF9800",
                                count: counters.revisiones
                            },
                            {
                                key: "todas",
                                icon: ViewList,
                                tooltip: "Todas las tareas",
                                color: "#2196F3",
                                count: null
                            },
                        ].map((filter) => {
                            const Icon = filter.icon;
                            const isActive = estadoChip === filter.key;

                            return (
                                <Tooltip key={filter.key} title={filter.tooltip} arrow>
                                    <IconButton
                                        onClick={() => setEstadoChip(filter.key)}
                                        sx={{
                                            position: 'relative',
                                            border: isActive ? '2px solid' : '1px solid',
                                            borderColor: isActive ? filter.color : 'divider',
                                            backgroundColor: isActive ? `${filter.color}15` : 'transparent',
                                            padding: '10px',
                                            '&:hover': {
                                                backgroundColor: isActive ? `${filter.color}25` : 'action.hover',
                                            }
                                        }}
                                    >
                                        <Icon
                                            fontSize="medium"
                                            sx={{
                                                color: isActive ? filter.color : 'action.active'
                                            }}
                                        />
                                        {filter.count !== null && filter.count > 0 && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: -6,
                                                    right: -6,
                                                    backgroundColor: filter.color,
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    minWidth: 16,
                                                    height: 16,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.6rem',
                                                    fontWeight: 'bold',
                                                    border: '1.5px solid white',
                                                    padding: '0 2px',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                }}
                                            >
                                                {filter.count}
                                            </Box>
                                        )}
                                    </IconButton>
                                </Tooltip>
                            );
                        })}

                        <Divider orientation="vertical" flexItem />
                        <TextField
                            size="small"
                            placeholder="Buscar (número, DNI, nombre, detalle...)"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ minWidth: 280 }}
                        />
                        <Button size="small" variant="outlined" onClick={fetchTareas}>🔄 Refrescar</Button>

                        {/* Botón de Estadísticas */}
                        <Tooltip title="Ver estadísticas" arrow>
                            <IconButton
                                size="small"
                                onClick={() => setStatsOpen(true)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    padding: '8px',
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                        borderColor: 'primary.main',
                                    }
                                }}
                            >
                                <Assessment fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* TABLA */}
                <TableContainer
                    ref={tableContainerRef}
                    component={Paper}
                    sx={{
                        borderRadius: 2,
                        maxHeight: "70vh",
                        overflowY: "auto",
                        overflowX: "hidden",
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={colSx.thId}>#</TableCell>
                                <TableCell sx={colSx.thNum}>Empleado</TableCell>
                                <TableCell sx={colSx.thDet}>Detalle</TableCell>
                                <TableCell sx={colSx.thArc}>Archivo</TableCell>
                                <TableCell sx={colSx.thEst}>Estado</TableCell>
                                <TableCell sx={colSx.thFec}>Fecha / Hora</TableCell>
                                <TableCell sx={colSx.thAcc} align="center">Acción</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {tareasPaginadas.map((t) => {
                                const fechaLocal = new Date(t.fecha);
                                const rowIsML = isML(t);
                                return (
                                    <TableRow
                                        key={t.id}
                                        hover
                                        sx={{
                                            backgroundColor: getRowColor(t),
                                        }}
                                    >
                                        <TableCell sx={{ width: colSx.thId.width }}>{t.id}</TableCell>

                                        <TableCell sx={{ width: colSx.thNum.width, maxWidth: colSx.thNum.maxWidth }}>
                                            {/* Nombre y Apellido - Principal */}
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'text.primary',
                                                    fontSize: '0.95rem',
                                                    lineHeight: 1.2,
                                                    mb: 0.2
                                                }}
                                            >
                                                {t.nombre || "-"} {t.apellido || ""}
                                            </Typography>

                                            {/* Legajo y DNI */}
                                            <Box sx={{ display: 'flex', gap: 1.5, mb: 0.2 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    <strong>Leg:</strong> {t.legajo || "-"}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    <strong>DNI:</strong> {t.dni || "-"}
                                                </Typography>
                                            </Box>

                                            {/* Sucursal */}
                                            {(t.sucursal_id || t.sucursal_nombre) && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.4,
                                                        mb: 0.2
                                                    }}
                                                >
                                                    <Business sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.disabled',
                                                            fontSize: '0.72rem'
                                                        }}
                                                    >
                                                        {t.sucursal_id && `${t.sucursal_id} - `}{t.sucursal_nombre || t.sucursal_id || "-"}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Teléfono clickeable */}
                                            {t.telefono && (
                                                <Tooltip title="Copiar teléfono" arrow>
                                                    <Box
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            cursor: 'pointer',
                                                            color: 'primary.main',
                                                            '&:hover': {
                                                                color: 'primary.dark',
                                                                backgroundColor: 'primary.lighter',
                                                            },
                                                            borderRadius: 1,
                                                            px: 0.75,
                                                            py: 0.3,
                                                            transition: 'all 0.2s',
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copiarTelefono(t.telefono);
                                                        }}
                                                    >
                                                        <Phone sx={{ fontSize: '0.95rem' }} />
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: '0.78rem',
                                                                letterSpacing: '0.02em'
                                                            }}
                                                        >
                                                            {t.telefono}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </TableCell>

                                        <TableCell sx={colSx.tdDet}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t.tipo_detalle || "-"}
                                            </Typography>
                                            <Typography variant="body2">{t.mensaje || "-"}</Typography>
                                            {t.observacion && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        whiteSpace: "pre-wrap",
                                                        fontSize: "0.8rem",
                                                        mt: 1,
                                                    }}
                                                >
                                                    {t.observacion || "-"}
                                                </Typography>
                                            )}
                                        </TableCell>

                                        <TableCell sx={{ width: colSx.thArc.width }}>
                                            {/* 📎 CASO 1: MÚLTIPLES ARCHIVOS */}
                                            {t.cantidad_archivos > 1 ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                    {/* Botón ver múltiples archivos */}
                                                    <Tooltip title={`Ver ${t.cantidad_archivos} archivos`}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                verArchivosMultiples(t.id);
                                                            }}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                // 🔴 Rojo si archivo llegó en distinto día
                                                                color: (() => {
                                                                    if (!t.fecha_recepcion_archivo || !t.fecha_pospuesto_archivo) return 'inherit';

                                                                    const fechaPospuesto = new Date(t.fecha_pospuesto_archivo);
                                                                    const fechaRecepcion = new Date(t.fecha_recepcion_archivo);

                                                                    const diaPospuesto = fechaPospuesto.toLocaleDateString('es-AR');
                                                                    const diaRecepcion = fechaRecepcion.toLocaleDateString('es-AR');

                                                                    return diaPospuesto !== diaRecepcion ? 'error.main' : 'inherit';
                                                                })()
                                                            }}
                                                        >
                                                            <AttachFileIcon fontSize="small" />
                                                            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                                                                ({t.cantidad_archivos})
                                                            </Typography>
                                                        </IconButton>
                                                    </Tooltip>

                                                    {/* 📅 Fecha de recepción (si aplica) */}
                                                    {t.fecha_recepcion_archivo && t.fecha_pospuesto_archivo && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: '0.65rem',
                                                                color: (() => {
                                                                    const fechaPospuesto = new Date(t.fecha_pospuesto_archivo);
                                                                    const fechaRecepcion = new Date(t.fecha_recepcion_archivo);

                                                                    const diaPospuesto = fechaPospuesto.toLocaleDateString('es-AR');
                                                                    const diaRecepcion = fechaRecepcion.toLocaleDateString('es-AR');

                                                                    return diaPospuesto !== diaRecepcion ? 'error.main' : 'text.secondary';
                                                                })(),
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {(() => {
                                                                const fechaPospuesto = new Date(t.fecha_pospuesto_archivo);
                                                                const fechaRecepcion = new Date(t.fecha_recepcion_archivo);

                                                                const diaPospuesto = fechaPospuesto.toLocaleDateString('es-AR');
                                                                const diaRecepcion = fechaRecepcion.toLocaleDateString('es-AR');

                                                                // Si es el mismo día, solo mostrar hora
                                                                if (diaPospuesto === diaRecepcion) {
                                                                    return fechaRecepcion.toLocaleTimeString('es-AR', {
                                                                        hour: '2-digit',
                                                                        minute: '2-digit',
                                                                        hour12: true
                                                                    });
                                                                } else {
                                                                    // Si es otro día, mostrar día y hora
                                                                    return fechaRecepcion.toLocaleDateString('es-AR', {
                                                                        day: '2-digit',
                                                                        month: '2-digit'
                                                                    }) + ' ' + fechaRecepcion.toLocaleTimeString('es-AR', {
                                                                        hour: '2-digit',
                                                                        minute: '2-digit',
                                                                        hour12: true
                                                                    });
                                                                }
                                                            })()}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : /* 👁️ CASO 2: UN SOLO ARCHIVO */ t.archivo ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                    {/* Contenedor de iconos (horizontal) */}
                                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                                        {/* Ícono VER */}
                                                        <Tooltip title="Ver adjunto">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    verArchivo(t.archivo);
                                                                }}
                                                                sx={{
                                                                    // 🔴 Ícono rojo si el archivo fue enviado en distinto día
                                                                    color: (() => {
                                                                        if (!t.fecha_recepcion_archivo || !t.fecha_pospuesto_archivo) return 'inherit';

                                                                        const fechaPospuesto = new Date(t.fecha_pospuesto_archivo);
                                                                        const fechaRecepcion = new Date(t.fecha_recepcion_archivo);

                                                                        const diaPospuesto = fechaPospuesto.toLocaleDateString('es-AR');
                                                                        const diaRecepcion = fechaRecepcion.toLocaleDateString('es-AR');

                                                                        return diaPospuesto !== diaRecepcion ? 'error.main' : 'inherit';
                                                                    })()
                                                                }}
                                                            >
                                                                <Visibility fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        {/* Ícono DESCARGAR */}
                                                        <Tooltip title="Descargar">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    descargarArchivo(t.archivo);
                                                                }}
                                                                sx={{
                                                                    // Mismo color que el ícono de ver
                                                                    color: (() => {
                                                                        if (!t.fecha_recepcion_archivo || !t.fecha_pospuesto_archivo) return 'inherit';

                                                                        const fechaPospuesto = new Date(t.fecha_pospuesto_archivo);
                                                                        const fechaRecepcion = new Date(t.fecha_recepcion_archivo);

                                                                        const diaPospuesto = fechaPospuesto.toLocaleDateString('es-AR');
                                                                        const diaRecepcion = fechaRecepcion.toLocaleDateString('es-AR');

                                                                        return diaPospuesto !== diaRecepcion ? 'error.main' : 'inherit';
                                                                    })()
                                                                }}
                                                            >
                                                                <Download fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>

                                                    {/* 📅 Mostrar fecha/hora de recepción del archivo (centrada debajo de ambos iconos) */}
                                                    {t.fecha_recepcion_archivo && t.fecha_pospuesto_archivo && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: '0.65rem',
                                                                color: (() => {
                                                                    const fechaPospuesto = new Date(t.fecha_pospuesto_archivo);
                                                                    const fechaRecepcion = new Date(t.fecha_recepcion_archivo);

                                                                    const diaPospuesto = fechaPospuesto.toLocaleDateString('es-AR');
                                                                    const diaRecepcion = fechaRecepcion.toLocaleDateString('es-AR');

                                                                    return diaPospuesto !== diaRecepcion ? 'error.main' : 'text.secondary';
                                                                })(),
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {(() => {
                                                                const fechaPospuesto = new Date(t.fecha_pospuesto_archivo);
                                                                const fechaRecepcion = new Date(t.fecha_recepcion_archivo);

                                                                const diaPospuesto = fechaPospuesto.toLocaleDateString('es-AR');
                                                                const diaRecepcion = fechaRecepcion.toLocaleDateString('es-AR');

                                                                // Si es el mismo día, solo mostrar hora
                                                                if (diaPospuesto === diaRecepcion) {
                                                                    return fechaRecepcion.toLocaleTimeString('es-AR', {
                                                                        hour: '2-digit',
                                                                        minute: '2-digit',
                                                                        hour12: true
                                                                    });
                                                                } else {
                                                                    // Si es otro día, mostrar día y hora en ROJO
                                                                    return fechaRecepcion.toLocaleDateString('es-AR', {
                                                                        day: '2-digit',
                                                                        month: '2-digit'
                                                                    }) + ' ' + fechaRecepcion.toLocaleTimeString('es-AR', {
                                                                        hour: '2-digit',
                                                                        minute: '2-digit',
                                                                        hour12: true
                                                                    });
                                                                }
                                                            })()}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : /* ⚪ CASO 3: SIN ARCHIVOS */ (
                                                <Tooltip title="Sin adjunto">
                                                    <span>
                                                        <IconButton size="small" disabled>
                                                            <VisibilityOff fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </TableCell>

                                        <TableCell sx={{ width: colSx.thEst.width }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                                <Typography>{t.estado}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t.resolucion || "-"}
                                                </Typography>

                                                {/* Badge de revisión */}
                                                {t.fue_reactivada === 1 && (
                                                    <Chip
                                                        icon={<AccessTime sx={{ fontSize: '0.8rem' }} />}
                                                        label="Revisión"
                                                        size="small"
                                                        sx={{
                                                            height: '20px',
                                                            fontSize: '0.7rem',
                                                            backgroundColor: '#FF9800',
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            '& .MuiChip-icon': {
                                                                color: 'white'
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ width: colSx.thFec.width, whiteSpace: "nowrap", verticalAlign: "top" }}>
                                            <Typography variant="body2">⏱️ {formatRelativeEs(t.fecha, nowTick)}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {fechaLocal.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })}{" "}
                                                {fechaLocal.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ width: colSx.thAcc.width }} align="center">

                                            {/* 🔒 ML (sector 16) SIN archivo → menú deshabilitado */}
                                            {sector === "16" && (!t.archivo || t.archivo.trim() === "") ? (
                                                <Tooltip title="Adjunto requerido para operar">
                                                    <span>
                                                        <IconButton size="small" disabled>
                                                            <MoreVert fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                /* Caso normal: menú habilitado */
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleMenuOpen(e, t)}
                                                >
                                                    <MoreVert fontSize="small" />
                                                </IconButton>
                                            )}

                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* 🆕 Paginación */}
                <TablePagination
                    component="div"
                    count={tareasFiltradas.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[25, 50, 100, 200]}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}
                />

                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                    <MenuItem onClick={() => handleAccion("resolver")}>Resolver</MenuItem>
                </Menu>

                {/* Modal: Medicina Laboral tiene botones especiales */}
                <Dialog open={!!selected && !anchorEl} onClose={closeModal} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ bgcolor: "#1976d2", color: "white" }}>
                        {sector === "16"
                            ? `Revisar tarea #${selected?.id} (Medicina Laboral)`
                            : `Resolver tarea #${selected?.id}`}
                    </DialogTitle>

                    <DialogContent dividers sx={{ p: 3 }}>
                        <Typography variant="body2" gutterBottom>
                            <strong>Tipo:</strong> {selected?.tipo_detalle || "-"}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                            <strong>Mensaje:</strong> {selected?.mensaje || "-"}
                        </Typography>
                        {/* Enviar notificación (recuadro ancho completo) */}
                        {sector !== "16" && selected?.tipo_detalle !== "registro_pendiente" && (
                            <Box sx={{ width: "100%", mb: 1 }}>
                                <Paper
                                    variant="standard"
                                    sx={{
                                        width: "100%",
                                        p: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Checkbox
                                        checked={enviarNotif}
                                        onChange={(e) => setEnviarNotif(e.target.checked)}
                                        size="small"
                                        sx={{ p: 0.2 }}
                                    />
                                    <Typography variant="body2" sx={{ m: 0, color: "red" }}>
                                        Enviar notificación al usuario por WhatsApp
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                        {selected?.tipo_detalle === "registro_pendiente" && (
                            <Box sx={{ display: "flex", gap: 2, mt: 1, mb: 1 }}>
                                <TextField
                                    label="DNI"
                                    value={dniRegistro}
                                    onChange={(e) => setDniRegistro(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                                <TextField
                                    label="Nombre y apellido"
                                    value={nombreRegistro}
                                    onChange={(e) => setNombreRegistro(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                        )}
                        <TextField
                            fullWidth
                            label="Observación"
                            value={observacion}
                            onChange={(e) => setObservacion(e.target.value)}
                            multiline
                            rows={3}
                        />

                        {/* Campos de revisión - Solo para Medicina Laboral */}
                        {sector === "16" && (
                            <>
                                <Divider sx={{ my: 2 }} />

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                                    📅 Programar revisión (opcional)
                                </Typography>

                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                                    <DatePicker
                                        label="Fecha de revisión"
                                        value={fechaRevision}
                                        onChange={(newValue) => setFechaRevision(newValue)}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: "small",
                                                helperText: "Fecha para revisar esta tarea nuevamente"
                                            }
                                        }}
                                        sx={{ mb: 1.5 }}
                                    />
                                </LocalizationProvider>

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={esRevisionFinal}
                                            onChange={(e) => setEsRevisionFinal(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            Esta es la revisión FINAL (no volver a revisar)
                                        </Typography>
                                    }
                                />
                            </>
                        )}

                        {/* Última acción y observaciones previas */}
                        {/* Última acción */}
                        {selected?.fecha_accion && (
                            <Box sx={{ mt: 2, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                <AccessTime fontSize="small" color="action" />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        fontStyle: "italic",
                                        fontSize: "0.75rem",
                                        color: "text.disabled",
                                    }}
                                >
                                    {(() => {
                                        const raw = selected.fecha_accion;
                                        const fecha = dayjs(
                                            raw,
                                            [
                                                "YYYY-MM-DD HH:mm:ss",
                                                "YYYY-MM-DDTHH:mm:ss",
                                                "YYYY-MM-DDTHH:mm:ssZ",
                                                "YYYY-MM-DDTHH:mm:ss.SSSZ",
                                            ]
                                        );
                                        if (!fecha.isValid()) {
                                            return `Última acción: — por ${selected.usuario_accion || "—"}`;
                                        }
                                        return `Última acción: ${fecha.format("DD/MM/YYYY, HH:mm")} por ${selected.usuario_accion || "—"
                                            }`;
                                    })()}
                                </Typography>
                            </Box>
                        )}

                        {/* Historial de observaciones */}
                        {selected?.observacion && (
                            <Box
                                sx={{
                                    border: "1px solid #ddd",
                                    borderRadius: 1,
                                    p: 1,
                                    mt: 1,
                                    backgroundColor: "#fafafa",
                                    maxHeight: 200,
                                    overflowY: "auto",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        fontFamily: "monospace",
                                        fontSize: "0.7rem",
                                    }}
                                >
                                    {selected.observacion}
                                </Typography>
                            </Box>
                        )}


                    </DialogContent>
                    <DialogActions>

                        {/* 🟦 CASO 1 — TAREA DE REGISTRO (solo RRHH, no ML) */}
                        {selected?.tipo_detalle === "registro_pendiente" && sector !== "16" && (
                            <>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircle />}
                                    onClick={() => handleResolver("registro_aprobar")}
                                >
                                    Activar usuario
                                </Button>

                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Cancel />}
                                    onClick={() => handleResolver("registro_rechazar")}
                                >
                                    Rechazar registro
                                </Button>

                                <Button onClick={closeModal} color="inherit">
                                    Cerrar
                                </Button>
                            </>
                        )}

                        {/* 🟩 CASO 2 — MEDICINA LABORAL (igual que antes) */}
                        {selected?.tipo_detalle !== "registro_pendiente" && sector === "16" && (
                            <>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircle />}
                                    onClick={() => handleResolver("aprobado")}
                                >
                                    Aprobar
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Cancel />}
                                    onClick={() => handleResolver("rechazado")}
                                >
                                    Rechazar
                                </Button>
                                {["aprobado", "rechazado"].includes(
                                    (selected?.resolucion || "").toLowerCase()
                                ) && (
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            onClick={() => handleResolver("revertir")}
                                        >
                                            Revertir
                                        </Button>
                                    )}
                                <Button onClick={closeModal} color="inherit">
                                    Cerrar
                                </Button>
                            </>
                        )}

                        {/* 🟧 CASO 3 — TAREAS NORMALES RRHH */}
                        {selected?.tipo_detalle !== "registro_pendiente" && sector !== "16" && (
                            <>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<CheckCircle />}
                                    onClick={() => handleResolver("toggle")}
                                >
                                    {selected?.estado?.toLowerCase() === "pendiente"
                                        ? "Marcar como resuelta"
                                        : "Volver a pendiente"}
                                </Button>
                                <Button onClick={closeModal} color="inherit">
                                    Cerrar
                                </Button>
                            </>
                        )}

                    </DialogActions>

                </Dialog>

                <Snackbar
                    open={snack.open}
                    autoHideDuration={3000}
                    onClose={handleSnackClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert onClose={handleSnackClose} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>
                        {snack.msg}
                    </Alert>
                </Snackbar>

                {/* Modal de Estadísticas */}
                <WATareasStats
                    open={statsOpen}
                    onClose={() => setStatsOpen(false)}
                    tareas={tareas}
                />

                {/* 📎 MODAL: Lista de archivos múltiples */}
                <Dialog
                    open={archivosModal.open}
                    onClose={cerrarModalArchivos}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ bgcolor: "#1976d2", color: "white", display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon />
                        Archivos adjuntos ({archivosModal.total})
                        {archivosModal.tareaId && (
                            <Typography variant="caption" sx={{ ml: 'auto', opacity: 0.8 }}>
                                Tarea #{archivosModal.tareaId}
                            </Typography>
                        )}
                    </DialogTitle>

                    <DialogContent sx={{ pt: 2 }}>
                        {archivosModal.archivos.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">
                                    No hay archivos para mostrar
                                </Typography>
                            </Box>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Archivo</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Tamaño</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Caption</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {archivosModal.archivos.map((archivo, idx) => (
                                        <TableRow key={archivo.id || idx} hover>
                                            <TableCell>{archivo.orden || idx + 1}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {archivo.archivo_nombre || 'Sin nombre'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        archivo.mime_type
                                                            ? archivo.mime_type.split('/')[1].toUpperCase()
                                                            : 'N/A'
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {archivo.tamaño_bytes
                                                        ? (archivo.tamaño_bytes / 1024).toFixed(1) + ' KB'
                                                        : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        maxWidth: 150,
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    title={archivo.caption || ''}
                                                >
                                                    {archivo.caption || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {archivo.fecha_subida
                                                        ? new Date(archivo.fecha_subida).toLocaleString('es-AR', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })
                                                        : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                    {/* Botón VER */}
                                                    <Tooltip title="Ver archivo">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => verArchivo(archivo.archivo_path)}
                                                        >
                                                            <Visibility fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {/* Botón DESCARGAR */}
                                                    <Tooltip title="Descargar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => descargarArchivo(archivo.archivo_path)}
                                                        >
                                                            <Download fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={cerrarModalArchivos} color="inherit">
                            Cerrar
                        </Button>
                    </DialogActions>
                </Dialog>




            </Box >
        </>
    );
}